<!-- -->
<!-- Copyright (c) 2024 Cozy Intellect Inc.-->
<!-- File Path: packages/ui/src/components/Dialogs/ExportToLinearDialog.svelte-->
<!-- -->

<script lang="ts">
	import AlertCircle from '@lucide/svelte/icons/alert-circle';
	import AlertTriangle from '@lucide/svelte/icons/alert-triangle';
	import ArrowRight from '@lucide/svelte/icons/arrow-right';
	import Check from '@lucide/svelte/icons/check';
	import CheckCircle2 from '@lucide/svelte/icons/check-circle-2';
	import CheckSquare from '@lucide/svelte/icons/check-square';
	import ChevronLeft from '@lucide/svelte/icons/chevron-left';
	import Info from '@lucide/svelte/icons/info';
	import LayoutDashboard from '@lucide/svelte/icons/layout-dashboard';
	import Plus from '@lucide/svelte/icons/plus';
	import Send from '@lucide/svelte/icons/send';
	import Tag from '@lucide/svelte/icons/tag';
	import X from '@lucide/svelte/icons/x';
	import XCircle from '@lucide/svelte/icons/x-circle';
	import Zap from '@lucide/svelte/icons/zap';
	import ZapOff from '@lucide/svelte/icons/zap-off';
	import { assetToTaskPlan } from '@repo/chatlib/src/utils/taskPlanAsset';
	import type { ExportToLinearResponse } from '@repo/common/types/Linear';
	import type { Task } from '@repo/common/types/Task';
	import { TaskPriority } from '@repo/common/types/Task';
	import type { TaskPlan } from '@repo/common/types/TaskSynthesis';
	import { exportTasksToLinear } from '@repo/sharedlib/src/actions/linear.actions';
	import { getAssetById } from '@repo/sharedlib/src/stores/assets.store';
	import {
		linearProviderStore,
		safeRefreshLinearData
	} from '@repo/sharedlib/src/stores/provider.linear.store';
	import { taskStore } from '@repo/sharedlib/src/stores/task.store';
	import { workspaceStore } from '@repo/sharedlib/src/stores/workspace.store';
	import { Button } from '@ui-components/ui/button';
	import * as Dialog from '@ui-components/ui/dialog';
	import * as Select from '@ui-components/ui/select';
	import { showErrorToast, showSuccessToast } from '@ui-lib/toast';
	import { fade, fly } from 'svelte/transition';

	interface Props {
		open?: boolean;
		taskIds?: string[];
		memoryId?: string;
		onClose: () => void;
	}

	let { open = $bindable(false), taskIds = [], memoryId, onClose }: Props = $props();

	let selectedTeamId = $state('');
	let selectedProjectId = $state('');
	let selectedStateId = $state('');
	let addWorkspaceLabel = $state(true);
	let selectedLabelIds = $state<string[]>([]);
	let isExporting = $state(false);
	let currentStep = $state(1);
	let excludedTaskIds = $state<string[]>([]);
	let taskPlans = $state<Record<string, TaskPlan>>({});
	let exportResults = $state<ExportToLinearResponse | null>(null);

	const tasksStore = taskStore.getTasksArray();

	let allSelectedTasks = $derived.by(() => {
		const allTasks = $tasksStore ?? [];
		if (taskIds.length > 0) {
			return allTasks.filter((task) => taskIds.includes(task.id));
		}
		if (memoryId) {
			return allTasks.filter((task) => task.memoryId === memoryId);
		}
		return [] as Task[];
	});

	let tasksToExport = $derived(allSelectedTasks.filter((t) => !excludedTaskIds.includes(t.id)));

	let selectedWorkspaceNames = $derived.by(() => {
		const names = new Set<string>();
		tasksToExport.forEach((task) => {
			const workspace = workspaceStore.getWorkspace(task.workspaceId || '');
			if (workspace?.name) {
				names.add(workspace.name);
			}
		});
		return Array.from(names);
	});

	let validation = $derived.by(() => {
		const tasks = tasksToExport;
		const errors = tasks.filter((t) => !t.name).length;
		const warnings = tasks.filter((t) => t.name && !taskPlans[t.id]).length;
		const ready = tasks.length - errors - warnings;
		return { ready, errors, warnings };
	});

	let teams = $derived($linearProviderStore.teamsData?.teams ?? []);
	let projects = $derived(
		selectedTeamId
			? ($linearProviderStore.teamsData?.projects ?? []).filter((p) => p.teamId === selectedTeamId)
			: []
	);
	let workflowStates = $derived(
		selectedTeamId
			? ($linearProviderStore.teamsData?.workflowStates ?? []).filter(
					(s) => s.teamId === selectedTeamId
				)
			: []
	);
	let selectedTeam = $derived(teams.find((t) => t.id === selectedTeamId));
	let defaultStateId = $derived(selectedTeam?.defaultIssueStateId);

	let filteredWorkflowStates = $derived(
		workflowStates.filter((s) => {
			if (defaultStateId) return s.id !== defaultStateId;
			return s.type !== 'backlog';
		})
	);
	let defaultStateName = $derived.by(() => {
		if (defaultStateId) {
			return workflowStates.find((s) => s.id === defaultStateId)?.name || 'Backlog';
		}
		return workflowStates.find((s) => s.type === 'backlog')?.name || 'Backlog';
	});
	let availableLabels = $derived.by(() => {
		const labels = $linearProviderStore.teamsData?.labels ?? [];
		if (!selectedTeamId) return labels;
		return labels.filter((label) => label.teamId === selectedTeamId);
	});
	let organizationName = $derived($linearProviderStore.providers[0]?.data?.organizationName ?? '');
	let organizationId = $derived($linearProviderStore.providers[0]?.data?.organizationId ?? '');
	let organizationEmail = $derived($linearProviderStore.providers[0]?.email ?? '');

	let defaultTeamId = $derived($linearProviderStore.providers[0]?.data?.defaultTeamId ?? '');

	$effect(() => {
		if (open) {
			// Always trigger a refresh when opening the dialog to ensure we have teams/projects
			safeRefreshLinearData();
		}
	});

	$effect(() => {
		if (open && !selectedTeamId) {
			selectedTeamId = defaultTeamId || teams[0]?.id || '';
		}
	});

	$effect(() => {
		if (open && allSelectedTasks.length > 0) {
			allSelectedTasks.forEach(async (task) => {
				if (!taskPlans[task.id]) {
					try {
						const asset = await getAssetById(task.id);
						if (asset && asset.assetType === 'taskplan') {
							taskPlans[task.id] = assetToTaskPlan(asset);
						}
					} catch (e) {
						console.debug('No task plan found for task:', task.id);
					}
				}
			});
		}
	});

	$effect(() => {
		if (selectedProjectId && !projects.some((project) => project.id === selectedProjectId)) {
			selectedProjectId = '';
		}
	});

	$effect(() => {
		if (selectedLabelIds.length === 0) return;
		const validIds = new Set(availableLabels.map((label) => label.id));
		const nextLabelIds = selectedLabelIds.filter((id) => validIds.has(id));
		if (nextLabelIds.length !== selectedLabelIds.length) {
			selectedLabelIds = nextLabelIds;
		}
	});

	const closeDialog = () => {
		open = false;
		currentStep = 1;
		excludedTaskIds = [];
		onClose();
	};

	const toggleTask = (taskId: string) => {
		if (excludedTaskIds.includes(taskId)) {
			excludedTaskIds = excludedTaskIds.filter((id) => id !== taskId);
		} else {
			excludedTaskIds = [...excludedTaskIds, taskId];
		}
	};

	const getPriorityInfo = (priority: TaskPriority | undefined) => {
		switch (priority) {
			case TaskPriority.URGENT:
				return {
					label: 'Urgent',
					color:
						'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400',
					icon: Zap
				};
			case TaskPriority.HIGH:
				return {
					label: 'High',
					color:
						'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400',
					icon: Zap
				};
			case TaskPriority.MEDIUM:
				return {
					label: 'Medium',
					color:
						'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400',
					icon: Zap
				};
			case TaskPriority.LOW:
				return {
					label: 'Low',
					color:
						'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400',
					icon: Zap
				};
			default:
				return {
					label: 'No priority',
					color:
						'text-gray-600 bg-gray-100 border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400',
					icon: ZapOff
				};
		}
	};

	const handleExport = async () => {
		if (!selectedTeamId || tasksToExport.length === 0 || isExporting) return;

		isExporting = true;

		try {
			const result = await exportTasksToLinear({
				tasks: tasksToExport.map((task) => ({
					taskId: task.id,
					title: task.name || task.description?.slice(0, 80) || 'Untitled task',
					description: task.description,
					priority: task.priority,
					workspaceName: workspaceStore.getWorkspace(task.workspaceId || '')?.name,
					taskPlan: taskPlans[task.id]
				})),
				teamId: selectedTeamId,
				projectId: selectedProjectId || undefined,
				stateId: selectedStateId || undefined,
				labelIds: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
				addWorkspaceAsLabel: addWorkspaceLabel
			});

			exportResults = result;
			currentStep = 3;

			if (result.success) {
				showSuccessToast(`Exported ${result.results.length} task(s) to Linear`);
			} else {
				const failed = result.results.filter((r) => !r.success).length;
				if (failed > 0) {
					showErrorToast(`Failed to export ${failed} task(s)`);
				} else {
					showSuccessToast('Export complete (some tasks skipped)');
				}
			}
		} catch (error) {
			showErrorToast('Export failed');
		} finally {
			isExporting = false;
		}
	};
</script>

<Dialog.Root bind:open onOpenChange={(val) => !val && onClose()}>
	<Dialog.Portal>
		<Dialog.Overlay class="bg-black/30 dark:bg-black/50" />
		<Dialog.DialogPrimitive.Content
			class="fixed left-[50%] top-[50%] z-50 flex w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden outline-none duration-200"
		>
			<div
				class="bg-background relative flex max-h-[90vh] flex-col overflow-hidden rounded-2xl shadow-2xl"
				in:fly={{ y: 20, duration: 300 }}
			>
				<!-- Modal Header -->
				<div class="border-border flex items-center justify-between border-b px-6 py-5">
					<div class="flex items-center gap-3">
						<div
							class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30"
						>
							<Send class="h-6 w-6 text-blue-600 dark:text-blue-400" />
						</div>
						<div>
							<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
								Export to Linear
							</h2>
							<p class="text-muted-foreground text-sm">
								Create Linear issues from your selected tasks
							</p>
						</div>
					</div>
					<button
						onclick={closeDialog}
						class="hover:bg-muted text-muted-foreground flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
					>
						<X class="h-5 w-5" />
					</button>
				</div>

				<!-- Step Indicator -->
				<div class="bg-muted/30 border-border border-b px-6 py-4">
					<div class="mx-auto flex max-w-md items-center justify-between">
						<div class="flex flex-1 flex-col items-center gap-2">
							<div
								class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors {currentStep >=
								1
									? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
									: 'bg-muted text-muted-foreground'}"
							>
								1
							</div>
							<span
								class="text-xs font-medium {currentStep >= 1
									? 'text-foreground'
									: 'text-muted-foreground'}">Review</span
							>
						</div>
						<div
							class="h-px flex-1 transition-colors {currentStep >= 2
								? 'bg-blue-600'
								: 'bg-border'} -mt-6"
						></div>
						<div class="flex flex-1 flex-col items-center gap-2">
							<div
								class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors {currentStep >=
								2
									? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
									: 'bg-muted text-muted-foreground'}"
							>
								2
							</div>
							<span
								class="text-xs font-medium {currentStep >= 2
									? 'text-foreground'
									: 'text-muted-foreground'}">Destination</span
							>
						</div>
						<div
							class="h-px flex-1 transition-colors {currentStep >= 3
								? 'bg-blue-600'
								: 'bg-border'} -mt-6"
						></div>
						<div class="flex flex-1 flex-col items-center gap-2">
							<div
								class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors {currentStep >=
								3
									? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
									: 'bg-muted text-muted-foreground'}"
							>
								3
							</div>
							<span
								class="text-xs font-medium {currentStep >= 3
									? 'text-foreground'
									: 'text-muted-foreground'}">Results</span
							>
						</div>
					</div>
				</div>

				<!-- Modal Body (Scrollable) -->
				<div class="flex-1 overflow-y-auto px-6 py-6">
					{#if currentStep === 1}
						<div class="space-y-4" in:fade={{ duration: 200 }}>
							<!-- Selection Summary -->
							<div
								class="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20"
							>
								<div class="flex items-center gap-3">
									<div
										class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/40"
									>
										<CheckSquare class="h-5 w-5 text-blue-600 dark:text-blue-400" />
									</div>
									<div>
										<p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
											{tasksToExport.length} tasks selected
										</p>
										<p class="text-muted-foreground text-xs">Local-first export</p>
									</div>
								</div>
								<div
									class="rounded-lg bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-600 dark:bg-blue-900/40 dark:text-blue-400"
								>
									Ready
								</div>
							</div>

							<!-- Helper Text -->
							<div class="bg-muted/30 border-border flex items-start gap-2 rounded-lg border p-3">
								<Info class="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
								<p class="text-muted-foreground text-xs">
									Review your tasks below. Only selected tasks will be created as Linear issues.
								</p>
							</div>

							<!-- Task Preview List -->
							<div class="space-y-3">
								{#each allSelectedTasks as task}
									{@const priorityInfo = getPriorityInfo(task.priority)}
									{@const isExcluded = excludedTaskIds.includes(task.id)}
									{@const workspace = workspaceStore.getWorkspace(task.workspaceId || '')}
									<div
										class="border-border group rounded-xl border p-4 transition-all {isExcluded
											? 'opacity-50 grayscale'
											: 'bg-background hover:border-blue-300 hover:shadow-lg dark:hover:border-blue-600'}"
									>
										<div class="flex items-start gap-3">
											<button onclick={() => toggleTask(task.id)} class="mt-1 flex-shrink-0">
												<div
													class="flex h-5 w-5 items-center justify-center rounded border-2 transition-colors {isExcluded
														? 'border-border bg-transparent'
														: 'border-blue-600 bg-blue-600 dark:border-blue-500 dark:bg-blue-500'}"
												>
													{#if !isExcluded}
														<Check class="h-3 w-3 text-white" />
													{/if}
												</div>
											</button>
											<div class="min-w-0 flex-1">
												<div class="mb-2 flex items-start justify-between gap-3">
													<h4
														class="line-clamp-2 text-sm font-medium {isExcluded
															? 'text-muted-foreground'
															: 'text-gray-900 dark:text-gray-100'}"
													>
														{task.name || '(Untitled task)'}
													</h4>
												</div>
												{#if task.description}
													<p class="text-muted-foreground mb-3 line-clamp-2 text-xs">
														{task.description}
													</p>
												{:else}
													<p class="text-muted-foreground mb-3 text-xs italic">
														No description provided
													</p>
												{/if}
												<div class="flex flex-wrap items-center gap-2">
													{#if workspace?.name}
														<div
															class="flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
														>
															<Tag class="h-3 w-3" />
															<span>{workspace.name}</span>
														</div>
													{/if}
													<div
														class="flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium {priorityInfo.color}"
													>
														<priorityInfo.icon class="h-3 w-3 fill-current" />
														<span>{priorityInfo.label}</span>
													</div>
													{#if !task.name}
														<div
															class="flex items-center gap-1 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
														>
															<AlertTriangle class="h-3 w-3" />
															<span>Missing title</span>
														</div>
													{:else if !taskPlans[task.id]}
														<div
															class="flex items-center gap-1 rounded border border-yellow-200 bg-yellow-50 px-2 py-1 text-xs text-yellow-600 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
														>
															<AlertCircle class="h-3 w-3" />
															<span>No task plan</span>
														</div>
													{:else}
														<div
															class="bg-muted text-muted-foreground flex items-center gap-1 rounded px-2 py-1 text-xs"
														>
															<CheckCircle2 class="h-3 w-3 text-green-500" />
															<span>Ready to export</span>
														</div>
													{/if}
												</div>
												{#if taskPlans[task.id]}
													<div class="mt-4 space-y-2 border-t pt-3">
														<p
															class="text-muted-foreground text-[10px] font-bold uppercase tracking-wider"
														>
															Task Plan Included
														</p>
														<div class="bg-muted/50 space-y-1 rounded-lg p-2 text-[11px]">
															<p class="text-foreground line-clamp-1 font-medium">
																{taskPlans[task.id].header.title}
															</p>
															<p class="text-muted-foreground line-clamp-2">
																{taskPlans[task.id].header.context}
															</p>
															<div class="mt-1 flex items-center gap-2">
																<span
																	class="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-600 dark:bg-blue-900/40 dark:text-blue-400"
																>
																	{taskPlans[task.id].actions.length} actions
																</span>
															</div>
														</div>
													</div>
												{/if}
											</div>
										</div>
									</div>
								{/each}
							</div>

							<!-- Validation Summary -->
							<div
								class="bg-muted/30 border-border flex items-center justify-between rounded-xl border p-4"
							>
								<div class="flex items-center gap-4 text-sm">
									<div class="flex items-center gap-2">
										<CheckCircle2 class="h-4 w-4 text-green-500" />
										<span class="text-muted-foreground">{validation.ready} ready</span>
									</div>
									<div class="flex items-center gap-2">
										<AlertCircle class="h-4 w-4 text-yellow-500" />
										<span class="text-muted-foreground">{validation.warnings} warnings</span>
									</div>
									<div class="flex items-center gap-2">
										<XCircle class="h-4 w-4 text-red-500" />
										<span class="text-muted-foreground">{validation.errors} errors</span>
									</div>
								</div>
								{#if validation.errors > 0}
									<p class="text-xs font-medium text-red-500">Please fix errors</p>
								{/if}
							</div>
						</div>
					{:else if currentStep === 2}
						<div class="space-y-6" in:fade={{ duration: 200 }}>
							<!-- Helper Text -->
							<div
								class="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20"
							>
								<Info class="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
								<p class="text-xs text-blue-900 dark:text-blue-100">
									Export is local-first—only these selected tasks will be created as issues. Choose
									where your issues should be created in Linear.
								</p>
							</div>

							<!-- Workspace Selection -->
							<div class="space-y-2">
								<label class="block text-sm font-semibold text-gray-900 dark:text-gray-100">
									Linear Workspace
									<span class="text-muted-foreground ml-1 font-normal">(optional)</span>
								</label>
								<div class="relative">
									<Select.Root type="single" value={organizationId}>
										<Select.Trigger
											class="bg-background flex w-full items-center justify-between rounded-lg border border-gray-300 px-4 py-8 transition-colors hover:border-blue-400 dark:border-gray-600 dark:hover:border-blue-500"
										>
											<div class="flex items-center gap-3">
												<div
													class="flex h-8 w-8 items-center justify-center rounded bg-gradient-to-br from-purple-500 to-blue-500 text-xs font-bold text-white"
												>
													{organizationName?.slice(0, 2).toUpperCase() || 'LW'}
												</div>
												<div class="text-left">
													<div class="text-sm font-medium text-gray-900 dark:text-gray-100">
														{organizationName || 'Linear Workspace'}
													</div>
													<div class="text-muted-foreground text-xs">{organizationEmail}</div>
												</div>
											</div>
										</Select.Trigger>
										<Select.Content>
											<Select.Item value={organizationId} label={organizationName} />
										</Select.Content>
									</Select.Root>
								</div>
								<p class="text-muted-foreground text-xs">
									Select which Linear workspace to export to. If you only have one workspace, it
									will be selected automatically.
								</p>
							</div>

							<!-- Team Selection (Required) -->
							<div class="space-y-2">
								<label class="block text-sm font-semibold text-gray-900 dark:text-gray-100">
									Team
									<span class="ml-1 text-red-500 dark:text-red-400">*</span>
								</label>
								<div class="relative">
									<Select.Root
										type="single"
										value={selectedTeamId}
										onValueChange={(value) => (selectedTeamId = value ?? '')}
									>
										<Select.Trigger
											class="bg-background flex w-full items-center justify-between rounded-lg border-2 border-blue-500 px-4 py-8 shadow-sm shadow-blue-900/10 transition-colors hover:border-blue-600 dark:border-blue-400 dark:shadow-blue-500/20 dark:hover:border-blue-300"
										>
											<div class="flex items-center gap-3">
												<div
													class="flex h-8 w-8 items-center justify-center rounded bg-gradient-to-br from-blue-500 to-cyan-500"
												>
													<LayoutDashboard class="h-4 w-4 text-white" />
												</div>
												<div class="text-left">
													<div class="text-sm font-medium text-gray-900 dark:text-gray-100">
														{teams.find((t) => t.id === selectedTeamId)?.name || 'Select Team'}
													</div>
													<div class="text-muted-foreground text-xs">
														{teams.find((t) => t.id === selectedTeamId)?.key || 'TEAM'} • Required
													</div>
												</div>
											</div>
										</Select.Trigger>
										<Select.Content>
											{#each teams as team}
												<Select.Item value={team.id} label={team.name} />
											{/each}
										</Select.Content>
									</Select.Root>
								</div>
								<p class="text-muted-foreground text-xs">
									Required. Issues will be created in this team's backlog.
								</p>
							</div>

							<!-- Workflow State Selection (Optional) -->
							<div class="space-y-2">
								<label class="block text-sm font-semibold text-gray-900 dark:text-gray-100">
									Workflow State
									<span class="text-muted-foreground ml-1 font-normal">(optional)</span>
								</label>
								<div class="relative">
									<Select.Root
										type="single"
										value={selectedStateId}
										onValueChange={(value) => (selectedStateId = value ?? '')}
									>
										<Select.Trigger
											class="bg-background flex h-16 w-full items-center justify-between rounded-lg border border-gray-300 px-4 transition-colors hover:border-blue-400 dark:border-gray-600 dark:hover:border-blue-500"
										>
											<div class="flex items-center gap-3">
												<div class="h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
												<div class="text-left">
													<div class="text-sm font-medium text-gray-900 dark:text-gray-100">
														{workflowStates.find((s) => s.id === selectedStateId)?.name ||
															defaultStateName}
													</div>
													{#if !selectedStateId}
														<div class="text-muted-foreground text-xs">
															Default state for new issues
														</div>
													{/if}
												</div>
											</div>
										</Select.Trigger>
										<Select.Content>
											<Select.Item value="" label="{defaultStateName} (Default)" />
											{#each filteredWorkflowStates as state}
												<Select.Item value={state.id} label={state.name} />
											{/each}
										</Select.Content>
									</Select.Root>
								</div>
								<p class="text-muted-foreground text-xs">
									Choose which workflow state to assign. Defaults to {defaultStateName} if not specified.
								</p>
							</div>

							<!-- Divider -->
							<div class="border-border border-t"></div>

							<!-- Labels Section -->
							<div class="space-y-4">
								<div>
									<h3 class="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
										Labels
									</h3>
									<p class="text-muted-foreground text-xs">
										Optionally add labels to help organize your issues in Linear.
									</p>
								</div>

								<!-- Add Workspace Label Checkbox -->
								<label
									class="bg-muted/30 border-border flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors hover:border-blue-300 dark:hover:border-blue-600"
								>
									<div class="flex h-5 items-center">
										<input
											type="checkbox"
											bind:checked={addWorkspaceLabel}
											class="border-border bg-background h-4 w-4 cursor-pointer rounded text-blue-600 focus:ring-2 focus:ring-blue-500"
										/>
									</div>
									<div class="flex-1">
										<div class="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">
											Add workspaces as labels
										</div>
										<p class="text-muted-foreground text-xs">
											Automatically tag issues with their respective workspace names for easy
											filtering in Linear.
										</p>
										{#if addWorkspaceLabel}
											<div class="mt-2 flex flex-wrap items-center gap-2">
												{#each selectedWorkspaceNames as name}
													<div
														class="inline-flex items-center gap-1.5 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
													>
														<Tag class="h-3 w-3" />
														<span>{name}</span>
													</div>
												{/each}
												{#if selectedWorkspaceNames.length > 0}
													<span class="text-muted-foreground text-xs"
														>Will be created if they don't exist</span
													>
												{/if}
											</div>
										{/if}
									</div>
								</label>

								<!-- Additional Labels (Optional) -->
								<div class="space-y-2">
									<label class="text-muted-foreground block text-sm font-medium">
										Additional labels
										<span class="ml-1 font-normal">(optional)</span>
									</label>
									<Select.Root type="multiple" bind:value={selectedLabelIds}>
										<Select.Trigger
											class="border-border hover:bg-muted/50 bg-background flex w-full items-center justify-between rounded-lg border border-dashed px-4 py-3 transition-colors hover:border-blue-400 dark:hover:border-blue-500"
										>
											<div class="text-muted-foreground flex items-center gap-2 text-sm">
												<Plus class="h-4 w-4" />
												<span>
													{selectedLabelIds.length > 0
														? `${selectedLabelIds.length} labels selected`
														: 'Select labels...'}
												</span>
											</div>
										</Select.Trigger>
										<Select.Content>
											{#each availableLabels as label}
												<Select.Item value={label.id} label={label.name}>
													{#snippet children()}
														<div class="flex items-center gap-2">
															<div
																class="h-2 w-2 rounded-full"
																style="background-color: {label.color}"
															></div>
															{label.name}
														</div>
													{/snippet}
												</Select.Item>
											{/each}
										</Select.Content>
									</Select.Root>
								</div>
							</div>

							<!-- Preview Summary -->
							<div
								class="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 p-4 dark:border-blue-800 dark:from-blue-900/20 dark:to-purple-900/20"
							>
								<div class="flex items-start gap-3">
									<div
										class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/40"
									>
										<Zap class="h-5 w-5 text-blue-600 dark:text-blue-400" />
									</div>
									<div class="min-w-0 flex-1">
										<h4 class="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
											Ready to export
										</h4>
										<div class="text-muted-foreground space-y-1.5 text-xs">
											<div class="flex items-center gap-2">
												<Check
													class="h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-400"
												/>
												<span>
													<span class="text-foreground font-semibold"
														>{tasksToExport.length} issues</span
													>
													will be created in
													<span class="text-foreground font-semibold">
														{teams.find((t) => t.id === selectedTeamId)?.name || 'Product Team'}
													</span>
												</span>
											</div>
											<div class="flex items-center gap-2">
												<Check
													class="h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-400"
												/>
												<span>
													State:
													<span class="text-foreground font-semibold">
														{workflowStates.find((s) => s.id === selectedStateId)?.name ||
															defaultStateName}
													</span>
												</span>
											</div>
											{#if addWorkspaceLabel || selectedLabelIds.length > 0}
												<div class="flex items-center gap-2">
													<Check
														class="h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-400"
													/>
													<span>
														Labels:
														<span class="text-foreground font-semibold">
															{[
																...(addWorkspaceLabel ? selectedWorkspaceNames : []),
																...selectedLabelIds.map(
																	(id) => availableLabels.find((l) => l.id === id)?.name
																)
															]
																.filter(Boolean)
																.join(', ')}
														</span>
													</span>
												</div>
											{/if}
										</div>
									</div>
								</div>
							</div>
						</div>
					{:else if currentStep === 3 && exportResults}
						<div class="space-y-6" in:fade={{ duration: 200 }}>
							<div
								class="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/50 py-10 dark:border-blue-900/30 dark:bg-blue-950/20"
							>
								<div
									class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400"
								>
									<CheckCircle2 class="h-10 w-10" />
								</div>
								<h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">Export Complete</h3>
								<p class="text-muted-foreground mt-1 text-center text-sm">
									{exportResults.results.filter((r) => r.success).length} of {exportResults.results
										.length} tasks processed successfully
								</p>
							</div>

							<div class="space-y-3">
								{#each exportResults.results as result}
									{@const task = allSelectedTasks.find((t) => t.id === result.taskId)}
									<div
										class="border-border flex items-center justify-between rounded-xl border p-4"
									>
										<div class="min-w-0 flex-1">
											<h4 class="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
												{task?.name || task?.description?.slice(0, 50) || 'Untitled task'}
											</h4>
											<div class="mt-1 flex items-center gap-2">
												{#if result.success}
													{#if result.issue}
														<span
															class="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-green-700 dark:bg-green-900/30 dark:text-green-400"
														>
															{result.issue.identifier}
														</span>
														<span class="text-muted-foreground text-[11px]">
															{result.issue.url ? 'Created issue' : 'Already existed'}
														</span>
													{:else}
														<span class="text-[11px] text-green-600 dark:text-green-400">
															Success
														</span>
													{/if}
												{:else}
													<span class="text-[11px] text-red-600 dark:text-red-400">
														Failed: {result.error || 'Unknown error'}
													</span>
												{/if}
											</div>
										</div>
										{#if result.success && result.issue?.url}
											<Button
												variant="ghost"
												size="sm"
												class="h-8 gap-1.5 text-blue-600 dark:text-blue-400"
												href={result.issue.url}
												target="_blank"
											>
												<span class="text-xs">View</span>
												<ArrowRight class="h-3 w-3" />
											</Button>
										{/if}
									</div>
								{/each}
							</div>
						</div>
					{/if}
				</div>

				<!-- Modal Footer -->
				<div class="bg-muted/30 border-border border-t px-6 py-4">
					<div class="flex items-center justify-between">
						{#if currentStep === 1}
							<button
								onclick={closeDialog}
								class="text-muted-foreground hover:bg-muted rounded-lg px-4 py-2 text-sm font-medium transition-colors"
							>
								Cancel
							</button>
							<button
								onclick={() => (currentStep = 2)}
								disabled={tasksToExport.length === 0 || validation.errors > 0}
								class="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-500 disabled:opacity-50"
							>
								<span>Continue</span>
								<ArrowRight class="h-4 w-4" />
							</button>
						{:else if currentStep === 2}
							<button
								onclick={() => (currentStep = 1)}
								class="text-muted-foreground hover:bg-muted flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
							>
								<ChevronLeft class="h-4 w-4" />
								<span>Back</span>
							</button>
							<button
								onclick={handleExport}
								disabled={!selectedTeamId || isExporting}
								class="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-500 disabled:opacity-50"
							>
								{#if isExporting}
									<div
										class="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white"
									></div>
									<span>Exporting...</span>
								{:else}
									<span>Export to Linear</span>
									<Send class="h-4 w-4" />
								{/if}
							</button>
						{:else if currentStep === 3}
							<div class="flex w-full justify-end">
								<button
									onclick={closeDialog}
									class="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-500"
								>
									<span>Done</span>
									<Check class="h-4 w-4" />
								</button>
							</div>
						{/if}
					</div>
				</div>
			</div>
		</Dialog.DialogPrimitive.Content>
	</Dialog.Portal>
</Dialog.Root>
